/**
 * OnboardingCompletionListener
 *
 * Bridge between the YAML onboarding plugin (which fires a window event from
 * outside the React tree) and the app's React tree.
 *
 * On `onboarding-yaml:complete`:
 *   1. Reads the *current* i18next.language at submit-time so the right
 *      config + fingerprint flow into the backend payload (works even after
 *      a language switch mid-flow).
 *   2. Submits via trpc.onboardingYaml.submit (provided by
 *      @xhubio-saas/onboarding-backend).
 *   3. On success: persists onboardingCompleted=true, which closes the
 *      OnboardingDialog over the already-loaded page. No navigation: the user
 *      is already standing on the app.
 *   4. On failure: shows a localized toast and leaves the dialog open —
 *      the OnboardingFlow's localStorage state still has the answers
 *      (locale-stable fingerprint + single key) so they can retry.
 *
 * Mounted as a wrappers[] entry in saas.config.ts. Wrappers sit ABOVE
 * RouterProvider, so router hooks are unavailable here — which is fine now
 * that completing the survey no longer navigates anywhere.
 */

import { usePreferences } from '@xhubio-saas/core-frontend/kernel'
import { configFingerprint } from '@xhubio-saas/core-frontend/plugin-onboarding-yaml'
import { toast } from '@xhubio-saas/core-frontend/ui-components'
import i18next from 'i18next'
import { useEffect } from 'react'
import { trpc } from '@/trpc'
import { onboardingConfigs } from '../onboarding/configs'
import { ONBOARDING_STORAGE } from '../onboarding/storage'

interface OnboardingCompletionListenerProps {
  children: React.ReactNode
}

interface CompletionDetail {
  answers: Record<string, string | number | boolean>
}

/**
 * djb2-derived 8-char hex hash. Stable, sync, no deps. Used to fit the
 * configFingerprint into the backend's `z.string().max(64)` constraint —
 * the upstream configFingerprint() string is ~100+ chars (id@v1 plus
 * step shape), too long for the DB column. Hashing preserves the
 * semantics we care about (same structural shape → same hash).
 */
function shortFingerprint(input: string): string {
  let h = 5381
  for (let i = 0; i < input.length; i++) {
    h = ((h * 33) ^ input.charCodeAt(i)) | 0
  }
  return `fp_${(h >>> 0).toString(16).padStart(8, '0')}`
}

// Module-level: pre-compute fingerprints. configFingerprint is locale-stable
// (only step IDs/types, no labels), so DE/EN normally produce the same hash —
// keeping per-locale lookup for future-proofing if someone diverges structure.
const fingerprints = {
  de: shortFingerprint(configFingerprint(onboardingConfigs.de)),
  en: shortFingerprint(configFingerprint(onboardingConfigs.en)),
}

function pickLocale(raw: string | undefined): 'de' | 'en' {
  return raw?.startsWith('de') ? 'de' : 'en'
}

export function OnboardingCompletionListener({ children }: OnboardingCompletionListenerProps) {
  const { setPreference } = usePreferences()

  // onboardingYaml isn't in the typed AppRouter yet (pending an
  // @xhubio-saas/onboarding-backend publish + AppRouter re-flow); cast this one
  // access until then.
  // biome-ignore lint/suspicious/noExplicitAny: onboardingYaml not yet in AppRouter
  const submit = (trpc as any).onboardingYaml.submit.useMutation()

  useEffect(() => {
    function handler(event: Event) {
      const detail = (event as CustomEvent<CompletionDetail>).detail
      // Read the user's *current* locale at submit-time. If they switched
      // languages mid-flow, this picks up the latest selection.
      const locale = pickLocale(i18next.language)
      const config = onboardingConfigs[locale]
      const fp = fingerprints[locale]

      void (async () => {
        try {
          await submit.mutateAsync({
            configId: config.id,
            configFingerprint: fp,
            answers: detail.answers,
          })
          // Flipping the preference is what closes the dialog — the page
          // underneath stays mounted, so the user keeps the playground they
          // were already looking at (conversion-first).
          await setPreference('onboardingCompleted', true)
          // Wipe the locally persisted onboarding draft so a future run
          // (e.g. after a backend reset that flips onboardingCompleted
          // back to false) starts at step 1 instead of resuming the prior
          // run's answers. Mid-flow resume (F5 on a half-filled form) still
          // works because save happens after every step — only the success
          // branch clears.
          ONBOARDING_STORAGE.clear()
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[onboarding] submit failed', err)
          toast.error(
            locale === 'de'
              ? 'Setup-Submit fehlgeschlagen — bitte erneut versuchen.'
              : 'Setup submit failed — please try again.'
          )
        }
      })()
    }
    window.addEventListener('onboarding-yaml:complete', handler)
    return () => window.removeEventListener('onboarding-yaml:complete', handler)
  }, [submit, setPreference])

  return <>{children}</>
}
